/**
 * ModalContext - Global modal state management
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode
} from 'react';

// ============================================================================
// TYPES
// ============================================================================

export type ModalType =
  | 'payment'
  | 'paymentStatus'
  | 'preclose'
  | 'moratorium'
  | 'insurance'
  | 'stopDebit'
  | 'voucher'
  | 'voucherGeneration'
  | 'emiSchedule'
  | 'loanDetails'
  | 'profile'
  | 'otp'
  | 'creditBalance'
  | 'personalLoan'
  | 'referAndEarn'
  | 'confirmation'
  | 'error'
  | 'success'
  | 'info'
  | 'custom';

export interface ModalConfig {
  id: string;
  type: ModalType;
  title?: string;
  data?: Record<string, unknown>;
  dismissible?: boolean;
  preventBackgroundClose?: boolean;
  onOpen?: () => void;
  onClose?: (result?: unknown) => void;
}

export interface ModalState {
  activeModal: ModalConfig | null;
  modalStack: ModalConfig[];
  isOpen: boolean;
}

export interface ModalContextType {
  state: ModalState;
  openModal: (config: ModalConfig) => void;
  closeModal: (result?: unknown) => void;
  closeAllModals: () => void;
  isModalOpen: (id: string) => boolean;
  getModalData: <T = unknown>(id: string) => T | null;
  updateModalData: (id: string, data: Record<string, unknown>) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const ModalContext = createContext<ModalContextType | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps): React.JSX.Element {
  const [state, setState] = useState<ModalState>({
    activeModal: null,
    modalStack: [],
    isOpen: false
  });

  // Keep track of pending close callbacks
  const closeCallbackRef = useRef<((result?: unknown) => void) | null>(null);

  /**
   * Open a modal
   */
  const openModal = useCallback((config: ModalConfig) => {
    setState(prev => {
      // If there's an active modal, push it to the stack
      const newStack = prev.activeModal 
        ? [...prev.modalStack, prev.activeModal]
        : prev.modalStack;

      return {
        activeModal: config,
        modalStack: newStack,
        isOpen: true
      };
    });

    // Call onOpen callback after state update
    setTimeout(() => {
      config.onOpen?.();
    }, 0);

    // Initialize Materialize modal if needed
    if (typeof window !== 'undefined' && (window as unknown as { M?: { Modal: { init: (el: Element) => void } } }).M) {
      const modalEl = document.getElementById(config.id);
      if (modalEl) {
        const M = (window as unknown as { M: { Modal: { init: (el: Element) => void } } }).M;
        M.Modal.init(modalEl);
      }
    }
  }, []);

  /**
   * Close the current modal
   */
  const closeModal = useCallback((result?: unknown) => {
    setState(prev => {
      // Store the close callback
      if (prev.activeModal?.onClose) {
        closeCallbackRef.current = prev.activeModal.onClose;
      }

      // Pop from stack if available
      if (prev.modalStack.length > 0) {
        const newStack = [...prev.modalStack];
        const previousModal = newStack.pop()!;
        
        return {
          activeModal: previousModal,
          modalStack: newStack,
          isOpen: true
        };
      }

      return {
        activeModal: null,
        modalStack: [],
        isOpen: false
      };
    });

    // Call the close callback after state update
    setTimeout(() => {
      closeCallbackRef.current?.(result);
      closeCallbackRef.current = null;
    }, 0);
  }, []);

  /**
   * Close all modals
   */
  const closeAllModals = useCallback(() => {
    setState(prev => {
      // Call close callbacks for all modals
      prev.modalStack.forEach(modal => modal.onClose?.());
      prev.activeModal?.onClose?.();

      return {
        activeModal: null,
        modalStack: [],
        isOpen: false
      };
    });
  }, []);

  /**
   * Check if a specific modal is open
   */
  const isModalOpen = useCallback((id: string): boolean => {
    return state.activeModal?.id === id || state.modalStack.some(m => m.id === id);
  }, [state]);

  /**
   * Get data for a specific modal
   */
  const getModalData = useCallback(<T = unknown>(id: string): T | null => {
    if (state.activeModal?.id === id) {
      return state.activeModal.data as T;
    }
    const stackModal = state.modalStack.find(m => m.id === id);
    return (stackModal?.data as T) || null;
  }, [state]);

  /**
   * Update data for the active modal
   */
  const updateModalData = useCallback((id: string, data: Record<string, unknown>) => {
    setState(prev => {
      if (prev.activeModal?.id === id) {
        return {
          ...prev,
          activeModal: {
            ...prev.activeModal,
            data: { ...prev.activeModal.data, ...data }
          }
        };
      }
      return prev;
    });
  }, []);

  // Handle escape key and background click
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state.isOpen && state.activeModal?.dismissible !== false) {
        closeModal();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isOpen, state.activeModal, closeModal]);

  const value: ModalContextType = {
    state,
    openModal,
    closeModal,
    closeAllModals,
    isModalOpen,
    getModalData,
    updateModalData
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useModal(): ModalContextType {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}

// ============================================================================
// HELPER HOOKS
// ============================================================================

/**
 * Hook for opening specific modal types with pre-configured settings
 */
export function useModalActions() {
  const { openModal, closeModal, state } = useModal();

  const openPaymentModal = useCallback((data: {
    loanId: string;
    amount: number;
    paymentType: string;
    loan: unknown;
  }, onClose?: (result?: unknown) => void) => {
    openModal({
      id: 'payment-modal',
      type: 'payment',
      title: 'Make Payment',
      data,
      dismissible: false,
      onClose
    });
  }, [openModal]);

  const openPaymentStatusModal = useCallback((data: {
    status: 'success' | 'failure' | 'pending';
    amount: number;
    transactionId?: string;
    message?: string;
  }, onClose?: () => void) => {
    openModal({
      id: 'payment-status-modal',
      type: 'paymentStatus',
      title: data.status === 'success' ? 'Payment Successful' : 'Payment Status',
      data,
      dismissible: true,
      onClose
    });
  }, [openModal]);

  const openPrecloseModal = useCallback((data: {
    loan: unknown;
    precloseAmount: number;
    details: unknown;
  }, onClose?: (result?: unknown) => void) => {
    openModal({
      id: 'preclose-modal',
      type: 'preclose',
      title: 'Preclose Loan',
      data,
      dismissible: false,
      onClose
    });
  }, [openModal]);

  const openEmiScheduleModal = useCallback((data: {
    loan: unknown;
    repayments: unknown[];
  }, onClose?: () => void) => {
    openModal({
      id: 'emi-schedule-modal',
      type: 'emiSchedule',
      title: 'EMI Schedule',
      data,
      dismissible: true,
      onClose
    });
  }, [openModal]);

  const openInsuranceModal = useCallback((data: {
    insuranceData: unknown;
    insuranceName: string;
  }, onClose?: () => void) => {
    openModal({
      id: 'insurance-modal',
      type: 'insurance',
      title: 'Insurance Details',
      data,
      dismissible: true,
      onClose
    });
  }, [openModal]);

  const openConfirmationModal = useCallback((data: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
  }, onClose?: (confirmed: boolean) => void) => {
    openModal({
      id: 'confirmation-modal',
      type: 'confirmation',
      title: data.title,
      data,
      dismissible: true,
      onClose: (result) => onClose?.(result as boolean)
    });
  }, [openModal]);

  const openErrorModal = useCallback((data: {
    title?: string;
    message: string;
  }, onClose?: () => void) => {
    openModal({
      id: 'error-modal',
      type: 'error',
      title: data.title || 'Error',
      data,
      dismissible: true,
      onClose
    });
  }, [openModal]);

  const openSuccessModal = useCallback((data: {
    title?: string;
    message: string;
  }, onClose?: () => void) => {
    openModal({
      id: 'success-modal',
      type: 'success',
      title: data.title || 'Success',
      data,
      dismissible: true,
      onClose
    });
  }, [openModal]);

  return {
    openPaymentModal,
    openPaymentStatusModal,
    openPrecloseModal,
    openEmiScheduleModal,
    openInsuranceModal,
    openConfirmationModal,
    openErrorModal,
    openSuccessModal,
    closeModal,
    isOpen: state.isOpen,
    activeModal: state.activeModal
  };
}
